import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Loader2, MessageSquare, Phone, Video, Calendar, Clock, 
  ArrowLeft, ArrowRight, Check, DollarSign
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_OPTIONS = [
  { value: 'TEXT', label: 'Text Consultation', icon: MessageSquare, description: 'Message-based consultation' },
  { value: 'AUDIO', label: 'Audio Call', icon: Phone, description: 'Phone or audio call' },
  { value: 'VIDEO', label: 'Video Call', icon: Video, description: 'Video conference (Zoom, Meet, etc.)' }
];

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function BookConsultationPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [provider, setProvider] = useState(null);
  const [availability, setAvailability] = useState(null);
  
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [availabilityMode, setAvailabilityMode] = useState(null); // 'OPEN_DOOR' or 'SCHEDULED'
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [clientQuestion, setClientQuestion] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');

  useEffect(() => {
    loadProvider();
  }, [slug]);

  useEffect(() => {
    if (user) {
      setClientName(user.name || user.ownerName || '');
      setClientEmail(user.email || '');
      setClientPhone(user.phone || '');
    }
  }, [user]);

  const loadProvider = async () => {
    try {
      // Get provider by slug
      const providerRes = await axios.get(`${API_URL}/api/providers/slug/${slug}`);
      setProvider(providerRes.data);
      
      // Get consultation availability
      const availRes = await axios.get(`${API_URL}/api/providers/${providerRes.data.id}/consultation-availability`);
      setAvailability(availRes.data);
      
      if (!availRes.data.offersConsultations) {
        toast.error('This provider does not offer consultations');
        navigate(`/provider/${slug}`);
      }
    } catch (error) {
      console.error('Error loading provider:', error);
      toast.error('Failed to load provider');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const getPricingForType = (type) => {
    if (!availability?.consultationPricing) return null;
    return availability.consultationPricing.find(p => p.consultationType === type);
  };

  const formatPrice = (pricing) => {
    if (!pricing || pricing.pricingType === 'FREE') return 'Free';
    const amount = (pricing.price / 100).toFixed(2);
    if (pricing.pricingType === 'HOURLY') return `$${amount}/hr`;
    return `$${amount}`;
  };

  const handleSubmit = async () => {
    if (!token) {
      toast.error('Please log in to book a consultation');
      navigate('/login', { state: { from: { pathname: `/provider/${slug}/consultation` } } });
      return;
    }

    if (!clientName || !clientEmail) {
      toast.error('Please provide your name and email');
      return;
    }

    setSubmitting(true);
    try {
      const bookingData = {
        providerId: provider.id,
        consultationType: selectedType,
        availabilityType: availabilityMode,
        clientQuestion,
        clientName,
        clientEmail,
        clientPhone
      };

      if (availabilityMode === 'OPEN_DOOR' && selectedBlock) {
        bookingData.openDoorBlockId = selectedBlock.id;
        bookingData.scheduledDate = selectedDate || new Date().toISOString().split('T')[0];
      } else if (availabilityMode === 'SCHEDULED' && selectedSlot) {
        bookingData.scheduledSlotId = selectedSlot.id;
        bookingData.scheduledDate = selectedSlot.date;
        bookingData.scheduledStart = selectedSlot.startTime;
        bookingData.scheduledEnd = selectedSlot.endTime;
      }

      await axios.post(`${API_URL}/api/consultation-bookings`, bookingData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Consultation request submitted!');
      navigate('/my-consultations');
    } catch (error) {
      console.error('Error booking consultation:', error);
      toast.error(error.response?.data?.detail || 'Failed to book consultation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!provider || !availability) {
    return null;
  }

  const availableTypes = TYPE_OPTIONS.filter(t => availability.consultationTypes?.includes(t.value));
  const canSelectOpenDoor = ['OPEN_DOOR', 'BOTH'].includes(availability.availabilityMode);
  const canSelectScheduled = ['SCHEDULED', 'BOTH'].includes(availability.availabilityMode);

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate(`/provider/${slug}`)}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to {provider.businessName}
          </Button>
          <h1 className="text-2xl font-bold mt-4">Book a Consultation</h1>
          <p className="text-muted-foreground">with {provider.businessName}</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-between mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}>
                {step > s ? <Check className="h-4 w-4" /> : s}
              </div>
              {s < 4 && <div className={`w-16 h-1 mx-2 ${step > s ? 'bg-primary' : 'bg-muted'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Choose Type */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Choose Consultation Type</CardTitle>
              <CardDescription>Select how you'd like to consult with {provider.ownerName}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {availableTypes.map((type) => {
                const Icon = type.icon;
                const pricing = getPricingForType(type.value);
                return (
                  <div
                    key={type.value}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedType === type.value ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground'
                    }`}
                    onClick={() => setSelectedType(type.value)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-muted rounded-full">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="font-medium">{type.label}</p>
                          <p className="text-sm text-muted-foreground">{type.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatPrice(pricing)}</p>
                        {pricing?.paymentTiming && pricing.paymentTiming !== 'FREE' && (
                          <p className="text-xs text-muted-foreground">
                            Payment {pricing.paymentTiming.toLowerCase()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <Button 
                className="w-full" 
                disabled={!selectedType}
                onClick={() => setStep(2)}
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Choose Availability Mode */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>Choose Availability</CardTitle>
              <CardDescription>When would you like to consult?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {canSelectOpenDoor && (
                <div
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    availabilityMode === 'OPEN_DOOR' ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground'
                  }`}
                  onClick={() => setAvailabilityMode('OPEN_DOOR')}
                >
                  <div className="flex items-center space-x-3">
                    <Clock className="h-5 w-5" />
                    <div>
                      <p className="font-medium">Open Door Hours</p>
                      <p className="text-sm text-muted-foreground">
                        Connect during provider's open consultation hours
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {canSelectScheduled && (
                <div
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    availabilityMode === 'SCHEDULED' ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground'
                  }`}
                  onClick={() => setAvailabilityMode('SCHEDULED')}
                >
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5" />
                    <div>
                      <p className="font-medium">Schedule Appointment</p>
                      <p className="text-sm text-muted-foreground">
                        Book a specific time slot
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex space-x-2">
                <Button variant="outline" onClick={() => setStep(1)}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button 
                  className="flex-1" 
                  disabled={!availabilityMode}
                  onClick={() => setStep(3)}
                >
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Select Time */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle>Select Time</CardTitle>
              <CardDescription>
                {availabilityMode === 'OPEN_DOOR' 
                  ? 'Choose an open door block' 
                  : 'Choose an available time slot'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {availabilityMode === 'OPEN_DOOR' && (
                <>
                  {availability.openDoorBlocks?.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">
                      No open door hours available
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {availability.openDoorBlocks.map((block) => (
                        <div
                          key={block.id}
                          className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                            selectedBlock?.id === block.id ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground'
                          }`}
                          onClick={() => setSelectedBlock(block)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{DAYS_OF_WEEK[block.dayOfWeek]}</p>
                              <p className="text-sm text-muted-foreground">
                                {block.startTime} - {block.endTime}
                              </p>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {block.consultationTypes?.join(', ')}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {availabilityMode === 'SCHEDULED' && (
                <>
                  {availability.scheduledSlots?.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">
                      No scheduled slots available
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {availability.scheduledSlots.map((slot) => (
                        <div
                          key={slot.id}
                          className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                            selectedSlot?.id === slot.id ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground'
                          }`}
                          onClick={() => setSelectedSlot(slot)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{slot.date}</p>
                              <p className="text-sm text-muted-foreground">
                                {slot.startTime} - {slot.endTime}
                              </p>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {slot.consultationTypes?.join(', ')}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              <div className="flex space-x-2">
                <Button variant="outline" onClick={() => setStep(2)}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button 
                  className="flex-1" 
                  disabled={
                    (availabilityMode === 'OPEN_DOOR' && !selectedBlock) ||
                    (availabilityMode === 'SCHEDULED' && !selectedSlot)
                  }
                  onClick={() => setStep(4)}
                >
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Details & Confirm */}
        {step === 4 && (
          <Card>
            <CardHeader>
              <CardTitle>Your Details</CardTitle>
              <CardDescription>Provide your information and question</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Your Name *</Label>
                  <Input
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    placeholder="email@example.com"
                  />
                </div>
              </div>
              <div>
                <Label>Phone (optional)</Label>
                <Input
                  type="tel"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <Label>Your Question or Topic</Label>
                <Textarea
                  value={clientQuestion}
                  onChange={(e) => setClientQuestion(e.target.value)}
                  placeholder="What would you like to discuss?"
                  rows={4}
                />
              </div>

              {/* Summary */}
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium mb-2">Booking Summary</p>
                <div className="space-y-1 text-sm">
                  <p><span className="text-muted-foreground">Type:</span> {selectedType}</p>
                  <p><span className="text-muted-foreground">Mode:</span> {availabilityMode?.replace('_', ' ')}</p>
                  {selectedSlot && (
                    <p><span className="text-muted-foreground">Time:</span> {selectedSlot.date} at {selectedSlot.startTime}</p>
                  )}
                  {selectedBlock && (
                    <p><span className="text-muted-foreground">Time:</span> {DAYS_OF_WEEK[selectedBlock.dayOfWeek]} {selectedBlock.startTime}-{selectedBlock.endTime}</p>
                  )}
                  <p><span className="text-muted-foreground">Price:</span> {formatPrice(getPricingForType(selectedType))}</p>
                </div>
              </div>

              {/* Meeting Instructions */}
              {availability.consultationInstructions && (
                <div className="p-4 border rounded-lg">
                  <p className="font-medium mb-2">Meeting Instructions</p>
                  <p className="text-sm whitespace-pre-wrap">{availability.consultationInstructions}</p>
                </div>
              )}

              <div className="flex space-x-2">
                <Button variant="outline" onClick={() => setStep(3)}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button 
                  className="flex-1" 
                  disabled={submitting || !clientName || !clientEmail}
                  onClick={handleSubmit}
                >
                  {submitting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="mr-2 h-4 w-4" />
                  )}
                  Confirm Booking
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
